import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { legalDocumentsApi, organizationApi } from '@/lib/pillarApi';
import {
    fromSystemDateTimeLocalInput,
    nowForSystemDateTimeLocalInput,
} from '@/lib/system-time-zone-datetime';
import { LegalAgreementImpactConfirmation } from './LegalAgreementImpactConfirmation';
import { SystemTimeZoneDateTimeField } from './SystemTimeZoneDateTimeField';
import { getOrganizationCapacityLabel } from '@/lib/organization-capacity';
import type {
    CreateLegalDocumentAssignmentRequest,
    LegalDocumentAssignmentPublic,
    LegalDocumentListItem,
    OrganizationOmnisearchListItem,
} from '@pillar-portal/api-types';
import {
    LegalDocumentAssignmentScope,
    LegalDocumentPurpose,
    Status,
} from '@pillar-portal/database/dist/frontend';
import { formatLegalDocumentPurpose } from './legal-document-purpose-format';

interface LegalDocumentAssignmentEditorProps {
    readonly legalDocument: LegalDocumentListItem;
    readonly onAssigned: (assignment: LegalDocumentAssignmentPublic) => void;
    /**
     * Fixes the assignment to this Organization, e.g. on its Organization
     * Details page, where no other scope or Organization makes sense.
     */
    readonly organization?: { organization_uuid: string; name: string };
}

const OrganizationUuidSchema = z.string().uuid();

function toIsoTimestamp(value: string): string {
    const timestamp = fromSystemDateTimeLocalInput(value);
    if (timestamp === null) {
        throw new Error('Enter a valid effective date and time.');
    }
    return timestamp;
}

export function LegalDocumentAssignmentEditor({
    legalDocument,
    onAssigned,
    organization,
}: LegalDocumentAssignmentEditorProps) {
    const [scope, setScope] = useState<LegalDocumentAssignmentScope>(
        legalDocument.legal_document_purpose === LegalDocumentPurpose.ORGANIZATION_AGREEMENT ||
            organization !== undefined
            ? LegalDocumentAssignmentScope.ORGANIZATION
            : LegalDocumentAssignmentScope.DEFAULT
    );
    const [organizationUuid, setOrganizationUuid] = useState(organization?.organization_uuid ?? '');
    const [organizationName, setOrganizationName] = useState(organization?.name ?? '');
    // A new assignment applies immediately unless the administrator picks a later time.
    const [inForceStart, setInForceStart] = useState(nowForSystemDateTimeLocalInput);
    const [inForceEnd, setInForceEnd] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pendingRequest, setPendingRequest] =
        useState<CreateLegalDocumentAssignmentRequest | null>(null);

    const mutation = useMutation({
        mutationFn: async (request: CreateLegalDocumentAssignmentRequest) =>
            await legalDocumentsApi.assignLegalDocument(request),
        onSuccess: (assignment) => {
            setErrorMessage(null);
            setPendingRequest(null);
            onAssigned(assignment);
        },
        onError: (error: unknown) => {
            setPendingRequest(null);
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Unable to create the legal document assignment.'
            );
        },
    });

    const isOrganizationAgreement =
        legalDocument.legal_document_purpose === LegalDocumentPurpose.ORGANIZATION_AGREEMENT;
    const organizationUuidParseResult = OrganizationUuidSchema.safeParse(organizationUuid);
    const canSubmit =
        !mutation.isPending &&
        inForceStart.length > 0 &&
        (scope === LegalDocumentAssignmentScope.DEFAULT || organizationUuidParseResult.success);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Assign this version</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="rounded border bg-gray-50 p-3 text-sm">
                    <div>
                        Version: {legalDocument.legal_document_title} (
                        {legalDocument.legal_document_version})
                    </div>
                    <div>
                        Purpose: {formatLegalDocumentPurpose(legalDocument.legal_document_purpose)}
                    </div>
                    <div>Digest: {legalDocument.content_sha256}</div>
                </div>

                {errorMessage === null ? null : (
                    <div
                        role="alert"
                        className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                    >
                        {errorMessage}
                    </div>
                )}

                {organization !== undefined ? (
                    <div className="text-sm">Organization: {organization.name}</div>
                ) : (
                    <div className="space-y-2">
                        <Label htmlFor="legal-assignment-scope">Scope</Label>
                        <select
                            id="legal-assignment-scope"
                            aria-label="Assignment scope"
                            className="w-full rounded-md border px-3 py-2"
                            value={scope}
                            disabled={isOrganizationAgreement}
                            onChange={(event) => {
                                const nextScope = z
                                    .enum(LegalDocumentAssignmentScope)
                                    .parse(event.target.value);
                                setScope(nextScope);
                                if (nextScope === LegalDocumentAssignmentScope.DEFAULT) {
                                    setOrganizationUuid('');
                                    setOrganizationName('');
                                }
                            }}
                        >
                            <option value={LegalDocumentAssignmentScope.DEFAULT}>
                                Default (all Organizations)
                            </option>
                            <option value={LegalDocumentAssignmentScope.ORGANIZATION}>
                                One Organization
                            </option>
                        </select>
                    </div>
                )}

                {organization === undefined &&
                scope === LegalDocumentAssignmentScope.ORGANIZATION ? (
                    <div className="space-y-2">
                        <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                            inputId="legal-assignment-organization"
                            label="Organization"
                            placeholder="Search Organizations by name..."
                            pageSize={25}
                            disabled={mutation.isPending}
                            selectedItemId={organizationUuid.length > 0 ? organizationUuid : null}
                            selectedItemLabel={
                                organizationName.length > 0 ? organizationName : undefined
                            }
                            onClearSelection={() => {
                                setOrganizationUuid('');
                                setOrganizationName('');
                            }}
                            fetchPage={async (params) => {
                                const response = await organizationApi.searchOrganizations({
                                    ...(params.terms !== undefined
                                        ? { terms: [...params.terms] }
                                        : {}),
                                    limit: params.limit,
                                    cursor: params.cursor,
                                });
                                return {
                                    items: response.organizations,
                                    pagination: response.pagination,
                                };
                            }}
                            getItemId={(item) => item.organization_uuid}
                            getItemLabel={(item) =>
                                `${item.name} (${getOrganizationCapacityLabel(item)})`
                            }
                            getItemFilterText={(item) => `${item.name} ${item.organization_uuid}`}
                            onSelect={(item) => {
                                setOrganizationUuid(item.organization_uuid);
                                setOrganizationName(item.name);
                            }}
                        />
                    </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-2">
                    <SystemTimeZoneDateTimeField
                        id="legal-assignment-start"
                        label="Effective from"
                        value={inForceStart}
                        onChange={setInForceStart}
                    />
                    <SystemTimeZoneDateTimeField
                        id="legal-assignment-end"
                        label="Effective until"
                        value={inForceEnd}
                        onChange={setInForceEnd}
                    />
                </div>

                {scope === LegalDocumentAssignmentScope.ORGANIZATION &&
                legalDocument.legal_document_purpose === LegalDocumentPurpose.USER_AGREEMENT ? (
                    <p className="text-sm">
                        Users of this Organization and its sub-Organizations must accept this
                        agreement. Once assigned, the platform default user agreements no longer
                        apply to this Organization or its sub-Organizations, and this document
                        replaces any inherited user agreement with the same title.
                    </p>
                ) : null}
                {isOrganizationAgreement ? (
                    <p className="text-sm">
                        A PRINCIPAL of this exact Organization must sign this agreement. It does not
                        apply to sub-Organizations and does not gate login.
                    </p>
                ) : null}

                <Button
                    disabled={!canSubmit || legalDocument.status !== Status.ACTIVE}
                    onClick={() => {
                        setErrorMessage(null);
                        try {
                            const assignmentInterval = {
                                legal_document_uuid: legalDocument.legal_document_uuid,
                                in_force_start: toIsoTimestamp(inForceStart),
                                ...(inForceEnd.length === 0
                                    ? {}
                                    : { in_force_end: toIsoTimestamp(inForceEnd) }),
                            };
                            const request: CreateLegalDocumentAssignmentRequest =
                                scope === LegalDocumentAssignmentScope.DEFAULT
                                    ? {
                                          ...assignmentInterval,
                                          scope: LegalDocumentAssignmentScope.DEFAULT,
                                          supersedes_default: false,
                                      }
                                    : {
                                          ...assignmentInterval,
                                          scope: LegalDocumentAssignmentScope.ORGANIZATION,
                                          organization_uuid:
                                              OrganizationUuidSchema.parse(organizationUuid),
                                          supersedes_default: isOrganizationAgreement
                                              ? false
                                              : true,
                                      };
                            setPendingRequest(request);
                        } catch (error: unknown) {
                            setErrorMessage(
                                error instanceof Error
                                    ? error.message
                                    : 'Unable to validate the assignment.'
                            );
                        }
                    }}
                >
                    {mutation.isPending ? 'Assigning...' : 'Create assignment'}
                </Button>
                {legalDocument.status !== Status.ACTIVE ? (
                    <p className="text-sm text-amber-700">
                        Activate this document version before assigning it.
                    </p>
                ) : null}
                <LegalAgreementImpactConfirmation
                    request={
                        pendingRequest === null
                            ? null
                            : { action: 'assign', assignment: pendingRequest }
                    }
                    title="Create this assignment?"
                    confirmLabel="Create assignment"
                    isConfirming={mutation.isPending}
                    onConfirm={() => {
                        if (pendingRequest !== null) {
                            mutation.mutate(pendingRequest);
                        }
                    }}
                    onCancel={() => setPendingRequest(null)}
                />
            </CardContent>
        </Card>
    );
}
